import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Label } from "../ui/label";
import { useCurrency, CurrencyCode } from "../../contexts/CurrencyContext";
import { toast } from "sonner";
import { Globe } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";

export const CurrencySwitcher = () => {
    const { t } = useLanguage();
    const { state, setSelectedCurrency } = useCurrency();

    const currencies = [
        { code: "DZD", name: t("settings.currency.dzd") || "Algerian Dinar", symbol: "د.ج" },
        { code: "USD", name: t("settings.currency.usd") || "US Dollar", symbol: "$" },
        { code: "EUR", name: t("settings.currency.eur") || "Euro", symbol: "€" },
    ];

    const handleSaveSettings = (e: React.FormEvent) => {
        e.preventDefault();
        toast.success(t("settings.currency.success") || "Currency settings saved successfully");
    };

    return (
        <Card id="currency-settings">
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <Globe className="w-5 h-5" />
                    {t("settings.currency.title")}
                </CardTitle>
            </CardHeader>
            <CardContent>
                <form onSubmit={handleSaveSettings} className="space-y-6">
                    <div>
                        <Label className="text-sm font-medium mb-3 block">
                            {t("settings.currency.exchangeRates") || "Default Exchange Rates"}
                        </Label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="p-3 bg-muted/50 rounded-lg flex items-center justify-between">
                                <span className="text-sm text-muted-foreground">{t("settings.currency.usd") || "US Dollar"}</span>
                                <span className="font-medium">{t("settings.currency.usdRate") || "1 USD ≈ 149 DZD"}</span>
                            </div>
                            <div className="p-3 bg-muted/50 rounded-lg flex items-center justify-between">
                                <span className="text-sm text-muted-foreground">{t("settings.currency.eur") || "Euro"}</span>
                                <span className="font-medium">{t("settings.currency.eurRate") || "1 EUR ≈ 164 DZD"}</span>
                            </div>
                        </div>
                        <p className="text-xs text-muted-foreground mt-3">
                            {t("settings.currency.exchangeRatesNote") || "* Rates are fixed relative to DZD for platform consistency."}
                        </p>
                    </div>

                    <div className="pt-6 border-t border-border">
                        <Label htmlFor="defaultCurrency">{t("settings.currency.default") || "Default Currency"}</Label>
                        <Select value={state.selectedCurrency} onValueChange={(value) => setSelectedCurrency(value as CurrencyCode)}>
                            <SelectTrigger className="w-full">
                                <SelectValue placeholder={t("settings.currency.select") || "Select Default Currency"} />
                            </SelectTrigger>
                            <SelectContent>
                                {currencies.map((curr) => (
                                    <SelectItem key={curr.code} value={curr.code}>
                                        {curr.name} ({curr.symbol})
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <p className="text-sm text-muted-foreground mt-2 font-normal">
                            {t("settings.currency.defaultHelp") || "This is the currency in which all transactions will be displayed by default"}
                        </p>
                    </div>

                    <div className="flex justify-end pt-6">
                        <Button type="submit">{t("settings.personalInfo.save") || "Save Changes"}</Button>
                    </div>
                </form>
            </CardContent>
        </Card>
    );
};
